import { AnyRouter } from '@tanstack/react-router'

import { ClientAppState, ClientAppStateInitParams, BaseTenant } from '@authy/lib-client/appState'
import { env } from '@authy/lib-client/env'

import { trackPageViews } from '@/utils/plausible'

import type {
    init,
    addIntegration,
    tanstackRouterBrowserTracingIntegration,
    ErrorBoundary as SentryErrorBoundary,
} from '@sentry/react'
import type { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import type {
    setUser,
    setTag,
    addBreadcrumb,
    getCurrentScope,
    captureException,
    extraErrorDataIntegration,
} from '@sentry/core'

type SentryCoreType = {
    setUser: typeof setUser
    setTag: typeof setTag
    addBreadcrumb: typeof addBreadcrumb
    getCurrentScope: typeof getCurrentScope
    captureException: typeof captureException
    extraErrorDataIntegration: typeof extraErrorDataIntegration
}

type SentryReactType = {
    init: typeof init
    addIntegration: typeof addIntegration
    tanstackRouterBrowserTracingIntegration: typeof tanstackRouterBrowserTracingIntegration
    ErrorBoundary: typeof SentryErrorBoundary
}

export type WebAppStateInitParams = ClientAppStateInitParams & {
    router: AnyRouter
}

export class WebAppState<TTenant extends BaseTenant = BaseTenant> extends ClientAppState<TTenant> {
    // @ts-expect-error its fine it will be assigned in init
    router: AnyRouter
    sentryReactPackage?: SentryReactType
    sentryCorePackage?: SentryCoreType
    loadPromise?: Promise<void>
    SentryErrorBoundaryComponent?: typeof SentryErrorBoundary
    ReactErrorBoundaryComponent?: typeof ReactErrorBoundary

    constructor({ useSentry = true }: { useSentry?: boolean } = {}) {
        super()
        this.loadPromise = this.load({ useSentry })
    }

    async load({ useSentry }: { useSentry?: boolean }) {
        if (useSentry) {
            this.sentryCorePackage = await import('@sentry/core')
            this.sentryReactPackage = await import('@sentry/react')

            const client = this.sentryReactPackage?.init({
                dsn: env.VITE_SENTRY_DSN_WEB,
                environment: this.isDevelopment ? 'development' : 'production',
                enabled: !this.isLocalhost,
                release: `authy@${env.VITE_PRODUCT_RELEASE}`,
                integrations: (integrations) => {
                    return [
                        ...integrations,
                        ...(this.sentryCorePackage
                            ? [
                                  this.sentryCorePackage.extraErrorDataIntegration({
                                      captureErrorCause: true,
                                  }),
                              ]
                            : []),
                    ]
                },
            })

            client?.on('afterSendEvent', (event) => {
                console.log('Sentry event id:', event.event_id)
            })

            // set the error boundary component
            if (this.sentryReactPackage) {
                this.SentryErrorBoundaryComponent = this.sentryReactPackage.ErrorBoundary
            }
        } else {
            const { ErrorBoundary } = await import('react-error-boundary')
            this.ReactErrorBoundaryComponent = ErrorBoundary
        }
    }

    async init({ router, ...otherParams }: WebAppStateInitParams) {
        if (this.loadPromise) {
            await this.loadPromise
        }

        await super.init({
            ...otherParams,
        })

        this.router = router

        // Page tracking
        trackPageViews(window.location.pathname) // track initial page

        router.subscribe('onResolved', (e) => {
            if (e.fromLocation?.pathname !== e.toLocation.pathname) {
                trackPageViews(e.toLocation.pathname)
            }
        })

        // add the integration with tanstack router to sentry
        this.sentryReactPackage?.addIntegration(
            this.sentryReactPackage.tanstackRouterBrowserTracingIntegration(this.router)
        )

        super.initSentry(this.sentryCorePackage)
    }
}
